import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import toast from 'react-hot-toast';
import { Button } from '../../../../components/Button/Button';
import axiosInstant from '../../../../api';
import {
  getServiceKeys,
  createServiceKey,
  deleteServiceKey,
} from '../../../../api/endpoints';
import { CreateApiKeyDialog } from '../apiKeys/CreateApiKeyDialog';
import { DeleteApiKeyDialog } from '../apiKeys/DeleteApiKeyDialog';

interface ServiceKey {
  id: string;
  token_prefix: string;
  label: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

// /settings/personal-keys — the "Personal API Keys" tab (internal staff only
// for now; router/admin.tsx + router/internalRoutes.tsx). A personal key (a
// service account key, API path /settings/service-keys) acts as its owner:
// an app sends it as a bearer token and can do what that person can do in
// the portal. Brand public-API keys are a separate feature (../apiKeys).
export default function ServiceKeysPage() {
  const [keys, setKeys] = useState<ServiceKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ServiceKey | null>(null);

  const fetchKeys = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstant.get(getServiceKeys());
      setKeys(res.data?.data ?? res.data ?? []);
    } catch (err) {
      toast.error('Failed to load personal API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const handleDeleted = () => {
    setDeleteTarget(null);
    fetchKeys();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-6">
        <div className="min-w-0">
          <h3 className="text-text-1 text-xl sm:text-3xl font-bold uppercase font-[CircularStd]">
            Personal API Keys
          </h3>
          <p className="text-text-2 text-[13px] sm:text-[14px] mt-1">
            Keys for apps you build. A key acts as you: it can do whatever you
            can do in the portal, and stops working as soon as you revoke it.
          </p>
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setShowCreateDialog(true)}
          className="!w-full sm:!w-auto !text-sm sm:!text-base"
        >
          Generate New Key
        </Button>
      </div>

      {loading && (
        <Box className="flex justify-center py-12">
          <CircularProgress />
        </Box>
      )}
      {!loading && keys.length === 0 && (
        <Box className="text-center py-12">
          <Typography className="text-muted text-[14px]">
            No personal API keys yet. Generate one to get started.
          </Typography>
        </Box>
      )}
      {!loading && keys.length > 0 && (
        <>
          {/* Desktop: table */}
          <TableContainer className="hidden md:block">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {keys.map((serviceKey) => (
                  <TableRow key={serviceKey.id}>
                    <TableCell>
                      <code className="text-[13px] bg-surface-2 px-2 py-1 rounded">
                        {serviceKey.token_prefix}••••
                      </code>
                    </TableCell>
                    <TableCell>{serviceKey.label || '—'}</TableCell>
                    <TableCell>{formatDate(serviceKey.created_at)}</TableCell>
                    <TableCell>{formatDate(serviceKey.last_used_at)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Revoke key">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(serviceKey)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile: cards */}
          <div className="md:hidden flex flex-col gap-3">
            {keys.map((serviceKey) => (
              <div
                key={serviceKey.id}
                className="border border-border-token rounded-xl p-4 bg-surface"
              >
                <div className="flex justify-between items-start gap-2 mb-3">
                  <code className="text-[13px] bg-surface-2 px-2 py-1 rounded break-all">
                    {serviceKey.token_prefix}••••
                  </code>
                  <Tooltip title="Revoke key">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(serviceKey)}
                      sx={{ color: 'error.main' }}
                      className="shrink-0"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
                <div className="flex flex-col gap-1.5 text-[13px]">
                  <div className="flex justify-between gap-2">
                    <span className="text-muted">Label</span>
                    <span className="text-right break-all">{serviceKey.label || '—'}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-muted">Created</span>
                    <span className="text-right">{formatDate(serviceKey.created_at)}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-muted">Last Used</span>
                    <span className="text-right">{formatDate(serviceKey.last_used_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <CreateApiKeyDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={fetchKeys}
        createUrl={createServiceKey()}
        requirePassword
        description="Generate a key for an app you're building. The app sends it as a bearer token (Authorization: Bearer <key>) and can do whatever you can do in the portal."
      />

      {deleteTarget && (
        <DeleteApiKeyDialog
          open={!!deleteTarget}
          apiKey={{
            id: deleteTarget.id,
            key_prefix: deleteTarget.token_prefix,
            label: deleteTarget.label,
          }}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
          deleteUrl={deleteServiceKey}
        />
      )}
    </div>
  );
}
